import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { moveOption, optionGroupId } from '@/lib/formBuilderDnd'
import { InlineOptionRow } from './InlineOptionRow'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { FormField, FormSection, FormMeta, SectionWidth, FieldType } from './types'
import { UserGroupMultiSelect } from './UserGroupMultiSelect'
import { LisStageSelect, LisTestCodeSelect, useActiveLisStageNames, useLisStageTests } from './LisStageSelect'
import { testCodeChoices } from '@/lib/lis/fieldStage'
import { useCellLibraryFields } from '@/hooks/useFieldLibrary'
import { VALUE_BEARING } from '@/lib/mappingTargets'
import { distinctSetNames, distinctSetPeers } from '@/lib/distinctSigners'
import {
  ATTACHMENT_MODES,
  ATTACHMENT_MODE_LABELS,
  attachmentFromMode,
  attachmentMode,
  isConstantField,
  supportsAttachment,
  type AttachmentMode,
} from '@/types/cgt'
import { useOptionSetsList } from '@/hooks/useOptionSets'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import type { RefFormGroup } from './FormFieldRefSelect'
import {
  FIELD_LABELS,
  isConvertibleFieldType,
  convertibleTypeOptions,
  NON_CONVERTIBLE_TYPE_HINT,
  toCgtFieldType,
} from './field-config'
import { FormulaBuilder } from './FormulaBuilder'
import { GateFormulaEditor } from './GateFormulaEditor'
import { tableFieldRefLabels } from './formulaRefPicker'
import { MultiSelect } from './MultiSelect'
import { VisibilityBuilder, type VisibilityPickTarget } from './VisibilityBuilder'
import { NotificationRuleBuilder, type NotificationPickTarget } from './NotificationRuleBuilder'
import { OosSpecBuilder } from './OosSpecBuilder'
import { oosFieldType, oosGradingType, oosOperatorsFor } from '@/lib/oos'
import { useTenant } from '@/providers/TenantProvider'
import { tenantsApi } from '@/api/tenantsApi'
import {
  prerequisiteIds,
  sectionPrerequisiteIds,
  wouldCreateVisibilityCycle,
  findFieldDependents,
  notificationRules,
  DEPENDENT_SURFACE_LABELS,
} from '@/lib/formSections'
import { TableConfigBuilder, makeDefaultTableConfig } from './TableConfigBuilder'
import { WidthSkeleton, SECTION_WIDTH_COLUMNS } from './WidthSkeleton'
import {
  SECTION_WIDTHS,
  SECTION_WIDTH_LABELS,
  SECTION_COLORS,
  SECTION_COLOR_LABELS,
  SECTION_COLOR_SWATCH_CLASS,
} from '@/lib/formSections'
import type { SectionColor } from './types'
import type { FormFieldLayout } from '@/types/cgt'
import { resolveLabelLayout, supportsLabelLayout } from '@/lib/fieldLayout'
import { LabelLayoutPicker } from './LabelLayoutPicker'

interface Props {
  field: FormField | null
  section: FormSection | null
  formMeta: FormMeta
  allFields: FormField[]
  /** Ordered sections — used to offer only EARLIER sections as prerequisites. */
  allSections: FormSection[]
  onFieldChange: (field: FormField) => void
  onSectionChange: (section: FormSection) => void
  onSectionDelete: (id: string) => void
  onFormMetaChange: (patch: Partial<FormMeta>) => void
  /** Visibility condition row currently armed for canvas click-to-pick. */
  visibilityPick: VisibilityPickTarget | null
  onVisibilityPickChange: (target: VisibilityPickTarget | null) => void
  /** Notification-rule condition row currently armed for canvas click-to-pick.
   *  Separate state from visibilityPick so the two builders' pick-arming never
   *  interferes with each other. */
  notificationPick: NotificationPickTarget | null
  onNotificationPickChange: (target: NotificationPickTarget | null) => void
  /**
   * Cross-form reference authoring (templates containing this form + upstream
   * fields). Unavailable → formulas and conditions stay same-form only.
   */
  crossForm?: CrossFormAuthoring
  /**
   * True once this form version has patient submissions (`formsApi.checkInUse`
   * in FormEditorPage) — this codebase's nearest existing analogue to a
   * draft/released version model. Locks the field-type control the same way
   * it already tells the author that saving now will create a new version.
   */
  formInUse?: boolean
  /** The form's label default from Form Settings (z8nrz7djyx); a field follows it unless it sets its own. */
  formFieldLayout?: FormFieldLayout | null
}

// Cross-form sources appear in the visibility pickers as pseudo-fields whose
// id is the full `handle.fieldId` ref key and whose label carries the form
// name. cgt 'dropdown' maps to the builder's 'select'; other type names align.
function crossFieldsAsFormFields(crossForm: CrossFormAuthoring | undefined): FormField[] {
  if (!crossForm?.available) return []
  return crossForm.allUpstreamFields.map(f => ({
    id: f.refKey,
    type: (f.fieldType === 'dropdown' ? 'select' : f.fieldType) as FormField['type'],
    label: f.label,
    required: false,
    placeholder: '',
    helpText: '',
    options: [],
  }))
}

// Upstream-form groups for the hierarchical form → field picker, scoped to the
// toolbar template context (empty when the context is None).
function crossFormGroups(crossForm: CrossFormAuthoring | undefined): RefFormGroup[] {
  if (!crossForm?.available || !crossForm.selectedProcedureTemplateId) return []
  return crossForm.upstreamForms.map(uf => ({
    key: uf.handle,
    name: uf.name,
    fields: uf.fields.map(f => ({
      refKey: f.refKey,
      label: f.label,
      type: f.fieldType === 'dropdown' ? 'select' : f.fieldType,
    })),
  }))
}

// The "This form" group for the hierarchical picker.
function selfFormGroup(name: string, fields: FormField[]): RefFormGroup {
  return {
    key: '',
    name: name ? `This form — ${name}` : 'This form',
    fields: fields.map(f => ({ refKey: f.id, label: f.label || 'Untitled', type: f.type })),
  }
}

/**
 * Read-only field ID with copy-to-clipboard — the token a hand-written
 * formula (a gate, or the Formula (advanced) box) must use to reference this
 * field, e.g. `{a1b2c3d4-...}`. The click-to-insert pickers never need this,
 * but nothing else in the builder surfaces the raw id, so an admin typing a
 * formula by hand (as opposed to clicking a field chip) had no way to find
 * it — see the field-ref pickers elsewhere in this panel for the click path.
 */
function FieldIdRow({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className='space-y-1.5'>
      <Label className='text-xs text-muted-foreground'>
        Field ID — use as <span className='font-mono'>{'{id}'}</span> in a hand-written formula
      </Label>
      <div className='flex items-center gap-1.5'>
        <Input
          readOnly
          value={id}
          onClick={e => e.currentTarget.select()}
          className='h-7 flex-1 cursor-text font-mono text-[11px] text-muted-foreground'
        />
        <Button
          type='button'
          size='icon'
          variant='ghost'
          className='h-7 w-7 shrink-0 text-muted-foreground'
          title='Copy field ID'
          onClick={() => {
            void navigator.clipboard.writeText(id)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          {copied ? <Check className='h-3.5 w-3.5 text-green-600' /> : <Copy className='h-3.5 w-3.5' />}
        </Button>
      </div>
    </div>
  )
}

export function PropertiesPanel({
  field,
  section,
  formMeta,
  allFields,
  allSections,
  onFieldChange,
  onSectionChange,
  onSectionDelete,
  onFormMetaChange,
  visibilityPick,
  onVisibilityPickChange,
  notificationPick,
  onNotificationPickChange,
  crossForm,
  formInUse,
  formFieldLayout,
}: Props) {
  // Option sets available for the picker (selection surface — uses the full list,
  // not ids-based, so the user can browse and choose).
  // TODO: paginate/search when >200 option sets
  const optionSetsList = useOptionSetsList({ take: 200 })
  const optionSets = optionSetsList.data?.items ?? []

  // Candidate recipients for the notification-rule builder — this tenant's
  // members + superadmins, merged server-side. Gated on forms.edit, so any
  // tenant admin who can reach the form builder can populate this picker.
  const { selectedTenantId } = useTenant()
  const notificationRecipients = useQuery({
    queryKey: ['tenants', selectedTenantId, 'notification-recipients'],
    queryFn: () => tenantsApi.getNotificationRecipients(selectedTenantId!),
    enabled: !!selectedTenantId,
  })

  // Field-type change (z8nrz7d0h6, Part 1) — armed by the type dropdown below,
  // applied only on confirmation so the author always sees who depends on the
  // field before the type actually changes.
  const [pendingTypeChange, setPendingTypeChange] = useState<FieldType | null>(null)
  const lisStages = useActiveLisStageNames()
  const lisStageTests = useLisStageTests()
  // Only a table field has cells to link; no need to page the whole library otherwise.
  const cellLibraryFields = useCellLibraryFields(field?.type === 'table').data ?? []

  // ─── Section selected: title + width + delete ──────────────────────────────
  if (section) {
    // Section-level sequencing prerequisites — only sections BEFORE this one are
    // eligible (a section can depend only on earlier sections). A section may
    // depend on several earlier sections (AND — it unlocks only when all complete).
    const sectionIdx = allSections.findIndex(s => s.id === section.id)
    const earlierSections = sectionIdx > 0 ? allSections.slice(0, sectionIdx) : []
    const selectedSectionPrereqs = sectionPrerequisiteIds(section)
    // Visibility sources for a section: any value-bearing field OUTSIDE it (a
    // section can't watch its own fields — they'd be hidden along with it),
    // excluding sources that would loop back through other visibility rules.
    const sectionSameFormSources = allFields.filter(
      f =>
        f.type !== 'table' &&
        f.type !== 'section' &&
        !section.fields.some(sf => sf.id === f.id) &&
        !wouldCreateVisibilityCycle(allSections, section.id, f.id),
    )
    // Fields from earlier forms in the workflow — same shared mechanism as
    // calculated formulas (cross-form cycles are impossible: only strictly
    // earlier forms are offered).
    const sectionVisibilitySources = [
      ...sectionSameFormSources,
      ...crossFieldsAsFormFields(crossForm),
    ]
    const sectionFormGroups = [
      selfFormGroup(formMeta.name, sectionSameFormSources),
      ...crossFormGroups(crossForm),
    ]
    return (
      <aside className='w-full overflow-y-auto flex-1'>
        <div className='px-4 py-3 border-b flex items-center gap-2'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-1'>
            Section Properties
          </p>
          <Badge variant='outline' className='text-xs'>
            {SECTION_WIDTH_LABELS[section.width]}
          </Badge>
        </div>
        <div className='p-4 space-y-4'>
          <div className='space-y-1.5'>
            <Label>Title</Label>
            <Input
              value={section.title}
              onChange={e => onSectionChange({ ...section, title: e.target.value })}
              placeholder='Section title (optional)…'
            />
            <p className='text-xs text-muted-foreground'>
              Shown as a heading above the section. Leave blank for no heading.
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label>Width</Label>
            <div className='grid grid-cols-2 gap-0.5'>
              {SECTION_WIDTHS.map((w: SectionWidth) => (
                <button
                  key={w}
                  type='button'
                  className={cn(
                    'flex flex-col items-center gap-1 rounded border px-1.5 py-1.5 text-[11px] hover:bg-accent',
                    section.width === w
                      ? 'border-primary bg-accent font-medium'
                      : 'border-transparent',
                  )}
                  onClick={() => onSectionChange({ ...section, width: w })}
                >
                  <WidthSkeleton columns={SECTION_WIDTH_COLUMNS[w]} />
                  {SECTION_WIDTH_LABELS[w]}
                </button>
              ))}
            </div>
            <p className='text-xs text-muted-foreground'>
              Sections pack into rows by width and wrap — two halves, three thirds,
              or four quarters sit side by side.
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label>Header Color</Label>
            <div className='flex flex-wrap gap-2'>
              {SECTION_COLORS.map((c: SectionColor) => {
                const active = (section.headerColor ?? 'none') === c
                return (
                  <button
                    key={c}
                    type='button'
                    title={SECTION_COLOR_LABELS[c]}
                    aria-label={SECTION_COLOR_LABELS[c]}
                    aria-pressed={active}
                    className={`h-6 w-6 rounded-full ${SECTION_COLOR_SWATCH_CLASS[c]} ring-offset-1 transition-all ${active ? 'ring-2 ring-primary' : 'hover:ring-2 hover:ring-muted-foreground/40'} focus-visible:ring-2 focus-visible:ring-primary`}
                    onClick={() => onSectionChange({ ...section, headerColor: c })}
                  />
                )
              })}
            </div>
            <p className='text-xs text-muted-foreground'>
              Tints the section header strip for visual grouping.
            </p>
          </div>

          {/* Section sequencing — lock this section until earlier section(s) complete */}
          {sectionIdx > 0 && (
            <div className='space-y-1.5'>
              <Label>Unlock after other sections are complete</Label>
              <MultiSelect
                options={earlierSections.map(s => ({
                  id: s.id,
                  label: s.title || 'Untitled section',
                }))}
                selected={selectedSectionPrereqs}
                onChange={ids =>
                  onSectionChange({
                    ...section,
                    unlockedBySections: ids.length ? ids : undefined,
                    // A non-empty explicit selection supersedes the legacy flag.
                    gatedByPrevious: ids.length ? undefined : section.gatedByPrevious,
                  })
                }
                placeholder='None — always unlocked'
                searchPlaceholder='Search sections…'
                emptyLabel='No sections match.'
              />
              <p className='text-xs text-muted-foreground'>
                {selectedSectionPrereqs.length === 0
                  ? 'Select one or more earlier sections; this section stays locked until they are all complete.'
                  : selectedSectionPrereqs.length === 1
                    ? 'This section stays locked until the selected section is complete.'
                    : `This section stays locked until all ${selectedSectionPrereqs.length} selected sections are complete.`}
              </p>
            </div>
          )}

          {/* Conditional visibility — value-driven show/hide for the whole section */}
          <VisibilityBuilder
            rule={section.visibility}
            onChange={rule => onSectionChange({ ...section, visibility: rule })}
            candidateFields={sectionVisibilitySources}
            formGroups={sectionFormGroups}
            targetLabel='section'
            allowRequire={false}
            pickArmed={visibilityPick}
            onArmPick={onVisibilityPickChange}
          />

          <Separator />

          <Button
            variant='outline'
            size='sm'
            className='w-full text-destructive hover:bg-destructive/10 hover:text-destructive'
            onClick={() => onSectionDelete(section.id)}
          >
            <Trash2 className='mr-1.5 h-3.5 w-3.5' />
            Delete section
          </Button>
          <p className='text-xs text-muted-foreground'>
            Deleting a section removes the fields inside it.
          </p>
        </div>
      </aside>
    )
  }

  if (!field) {
    return (
      <aside className='w-full overflow-y-auto flex-1'>
        <div className='px-4 py-3 border-b'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
            Form Properties
          </p>
        </div>
        <div className='p-4 space-y-4'>
          <div className='space-y-1.5'>
            <Label>Form Name</Label>
            <Input
              value={formMeta.name}
              onChange={e => onFormMetaChange({ name: e.target.value })}
              placeholder='Form name…'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Description</Label>
            <Textarea
              value={formMeta.description}
              onChange={e => onFormMetaChange({ description: e.target.value })}
              placeholder='What is this form for…'
              rows={3}
            />
          </div>
          <Separator />
          <p className='text-xs text-muted-foreground leading-relaxed'>
            Click a field on the canvas to edit its properties.
          </p>
        </div>
      </aside>
    )
  }

  const isSection = field.type === 'section'
  const isCalculated = field.type === 'calculated'
  const isTable = field.type === 'table'
  // A Review field (z8nrz7dt41) is a sign-off given after completion by an
  // assigned reviewer — nothing about it describes a value, so like a table
  // its own block replaces the value controls.
  const isReview = field.type === 'review'
  // A description says something; it does not collect anything. Everything that
  // describes how a VALUE behaves — required, an attachment, write-once, being a
  // prerequisite or a visibility source — is withheld from it.
  const isConstant = isConstantField(field.type)
  // Write-once is meaningless where the value isn't operator-typed: calculated
  // fields are recomputed server-side, signatures already have their own
  // overwrite rule, process fields follow step execution, and MR fields are
  // read-only already. Mirrors `supportsWriteOnce` in lib/fieldLock.ts.
  const supportsCanChange =
    !isCalculated &&
    !isConstant &&
    field.type !== 'signature' &&
    field.type !== 'process' &&
    field.type !== 'attachment' &&
    !field.isMrField
  // Only a field a laboratory value can be mapped INTO has a stage. A table's
  // stage is set per column and per cell, in its own designer.
  const supportsLisStage = VALUE_BEARING.has(toCgtFieldType(field.type))
  const hasPlaceholder = ['text', 'number', 'date', 'datetime', 'textarea'].includes(field.type)
  const hasOptions = field.type === 'select'

  // Sequencing prerequisites — only fields BEFORE this one are eligible, and only
  // value-bearing types (table values live under composite cell keys and can never
  // satisfy the gate; sections have no value at all; an image's picture is a
  // FormAttachment, so it reads as empty forever and would lock the dependent
  // field permanently). A field may depend on several earlier fields (AND — it
  // unlocks only when all of them are filled).
  const fieldIdx = allFields.findIndex(f => f.id === field.id)
  const earlierFields = fieldIdx > 0
    ? allFields
        .slice(0, fieldIdx)
        .filter(
          f =>
            f.type !== 'table' &&
            f.type !== 'section' &&
            f.type !== 'image' &&
            f.type !== 'attachment' &&
            f.type !== 'description' &&
            f.type !== 'review',
        )
    : []
  const selectedPrereqs = prerequisiteIds(field)

  // Visibility sources: ANY value-bearing field (order doesn't matter, unlike
  // sequencing) except the field itself and anything that would loop back to it
  // through other visibility rules (A↔B deadlocks are rejected up front).
  const sameFormVisibilitySources = allFields.filter(
    f =>
      f.id !== field.id &&
      f.type !== 'table' &&
      f.type !== 'section' &&
      // Same reason as the prerequisite filter: no value in the map to test.
      f.type !== 'image' &&
      f.type !== 'attachment' &&
      f.type !== 'description' &&
      f.type !== 'review' &&
      !wouldCreateVisibilityCycle(allSections, field.id, f.id),
  )
  // Fields from earlier forms in the workflow — same shared mechanism as
  // calculated formulas (only strictly earlier forms are offered).
  const visibilitySources = [
    ...sameFormVisibilitySources,
    ...crossFieldsAsFormFields(crossForm),
  ]
  const fieldFormGroups = [
    selfFormGroup(formMeta.name, sameFormVisibilitySources),
    ...crossFormGroups(crossForm),
  ]
  // Label map for formula-gate human-readable summaries — same source fields
  // offered by the visibility/formula pickers above.
  // Table cells and totals count as known refs, as in a calculated field.
  const gateLabelById = {
    ...tableFieldRefLabels(allFields),
    ...Object.fromEntries(visibilitySources.map(f => [f.id, f.label || 'Untitled'])),
  }

  // Notification-rule sources: deliberately NOT run through
  // wouldCreateVisibilityCycle — a notification rule doesn't hide/show
  // anything, so it can't create a visibility cycle, and unlike visibility,
  // referencing this field's own value is legal and harmless here.
  const notificationSources = [
    ...allFields.filter(f => f.type !== 'table' && f.type !== 'section'),
    ...crossFieldsAsFormFields(crossForm),
  ]

  // Authoring-time warning: a required field gated behind an optional field can
  // block form completion if that field is left empty.
  const showRequiredWarning =
    field.required &&
    selectedPrereqs.some(id => {
      const pf = allFields.find(f => f.id === id)
      return pf !== undefined && !pf.required
    })

  // Tables are full inline editors (columns/rows/cells); the standard narrow
  // sidebar clips them. Expand the panel for table fields so they fit.
  return (
    <aside className='w-full overflow-y-auto flex-1'>
      <div className='px-4 py-3 border-b flex items-center gap-2'>
        <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-1'>
          Field Properties
        </p>
        <Badge variant='outline' className='text-xs'>
          {FIELD_LABELS[field.type]}
        </Badge>
      </div>

      <div className='p-4 space-y-4'>
        {/* Field type -- editable for the five convertible scalar types;
            read-only with a hint otherwise. The field id (and every other
            property) survives a type change untouched — see
            findFieldDependents/convertibleTypeOptions (z8nrz7d0h6). */}
        <div className='space-y-1.5'>
          <Label htmlFor='field-type-select'>Field type</Label>
          {isConvertibleFieldType(field.type) ? (
            formInUse ? (
              <>
                <Input readOnly value={FIELD_LABELS[field.type]} className='h-8 text-sm text-muted-foreground' />
                <p className='text-xs text-muted-foreground'>
                  Type is fixed once this form version has patient submissions. Saving now
                  creates a new version where it can be changed.
                </p>
              </>
            ) : (
              <Select
                value={field.type}
                onValueChange={(v: FieldType) => {
                  if (v === field.type) return // AC3.4 — reselecting the current type is a no-op
                  setPendingTypeChange(v)
                }}
              >
                <SelectTrigger id='field-type-select' className='h-8 text-sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {convertibleTypeOptions(field.type).map(t => (
                    <SelectItem key={t} value={t}>
                      {FIELD_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : (
            <>
              <Input readOnly value={FIELD_LABELS[field.type]} className='h-8 text-sm text-muted-foreground' />
              <p className='text-xs text-muted-foreground'>
                {NON_CONVERTIBLE_TYPE_HINT[field.type] ?? "This field type can't be changed."}
              </p>
            </>
          )}
        </div>

        {/* Label. A text block has no label on the form, so for one this is
            only the name the builder knows it by. */}
        <div className='space-y-1.5'>
          <Label htmlFor='field-label'>{isConstant ? 'Name' : 'Label'}</Label>
          <Input
            id='field-label'
            value={field.label}
            onChange={e => onFieldChange({ ...field, label: e.target.value })}
            placeholder='Field label…'
          />
          {isConstant && (
            <p className='text-xs text-muted-foreground'>For the builder only — not shown on the form.</p>
          )}
        </div>

        {isConstant && (
          <div className='space-y-1.5'>
            <Label htmlFor='text-block-text'>Text</Label>
            <Textarea
              id='text-block-text'
              rows={6}
              value={field.constantText ?? ''}
              onChange={e => onFieldChange({ ...field, constantText: e.target.value })}
              placeholder='Standing instructions, a summary, a caveat…'
            />
            <p className='text-xs text-muted-foreground'>
              Shown on the form as written, line breaks included. It captures no
              answer: nothing is saved against it, and it is never required.
            </p>
          </div>
        )}

        {!isSection && <FieldIdRow id={field.id} />}

        {/* Table config -- replaces all other controls */}
        {isTable && (
          <TableConfigBuilder
            lisStages={lisStages}
            lisStageTests={lisStageTests}
            libraryFields={cellLibraryFields}
            config={field.tableConfig ?? makeDefaultTableConfig()}
            onChange={tc => onFieldChange({ ...field, tableConfig: tc })}
            tableFieldId={field.id}
            allFields={allFields}
            crossForm={crossForm}
            currentFormName={formMeta.name}
          />
        )}

        {isReview && (
          <div className='space-y-3'>
            <div className='space-y-1.5'>
              <Label>Help text</Label>
              <Input
                value={field.helpText ?? ''}
                onChange={e => onFieldChange({ ...field, helpText: e.target.value })}
                placeholder='What the reviewer is attesting to…'
              />
            </div>
            <p className='rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground'>
              Shown in a grey Review area at the bottom of the form. A reviewer can be
              assigned as soon as the form is started, and signs it with their password
              once the form is completed. The reviewer can never be the person who
              performs this step. Steps after this form stay locked until every review is
              signed.
            </p>
          </div>
        )}

        {!isTable && !isReview && (
          <>
            {/* Required toggle -- not applicable to sections, descriptions,
                calculated, or table fields */}
            {!isSection && !isConstant && (
              isCalculated ? (
                <p className='rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground'>
                  Calculated fields are read-only and auto-updated, so they can't be
                  marked required.
                </p>
              ) : (
                <div className='flex items-center justify-between'>
                  <Label className='font-normal cursor-pointer' htmlFor='required-toggle'>
                    Required
                  </Label>
                  <Switch
                    id='required-toggle'
                    checked={field.required}
                    onCheckedChange={v => onFieldChange({ ...field, required: v })}
                  />
                </div>
              )
            )}

            {/* Attachment — one three-value choice (None / Enabled / Required),
                sitting directly under Required as in the spec. Same exclusions
                as Required (see supportsAttachment). 'None' stores no config at
                all, so `required` can never be orphaned behind a disabled flag. */}
            {supportsAttachment(field.type) && (
              <div className='space-y-1.5'>
                <Label htmlFor='attachment-mode'>Attachment</Label>
                <Select
                  value={attachmentMode(field.attachment)}
                  onValueChange={(m: AttachmentMode) =>
                    onFieldChange({ ...field, attachment: attachmentFromMode(m) })
                  }
                >
                  <SelectTrigger id='attachment-mode'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ATTACHMENT_MODES.map(m => (
                      <SelectItem key={m} value={m}>
                        {ATTACHMENT_MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Field width within its section (tables always span full) */}
            {!isSection && !isTable && (
              <div className='space-y-1.5'>
                <Label>Width</Label>
                <div className='grid grid-cols-2 gap-0.5'>
                  {SECTION_WIDTHS.map((w: SectionWidth) => (
                    <button
                      key={w}
                      type='button'
                      className={cn(
                        'flex flex-col items-center gap-1 rounded border px-1.5 py-1.5 text-[11px] hover:bg-accent',
                        (field.fieldWidth ?? (field.colSpan === 1 ? 'half' : 'full')) === w
                          ? 'border-primary bg-accent font-medium'
                          : 'border-transparent',
                      )}
                      onClick={() => onFieldChange({ ...field, fieldWidth: w })}
                    >
                      <WidthSkeleton columns={SECTION_WIDTH_COLUMNS[w]} />
                      {SECTION_WIDTH_LABELS[w]}
                    </button>
                  ))}
                </div>
                <p className='text-xs text-muted-foreground'>
                  Controls how wide the field spans within its section.
                </p>
              </div>
            )}

            {/* Label position and width (z8nrz7djyx). Tables, signatures and
                fields without a label row keep their own layout. */}
            {supportsLabelLayout(field.type) && (() => {
              const labelLayout = resolveLabelLayout(field, formFieldLayout)
              const custom = labelLayout.source === 'field'
              return (
                <div className='space-y-2'>
                  <LabelLayoutPicker
                    position={labelLayout.position}
                    width={labelLayout.width}
                    onPositionChange={labelPosition => onFieldChange({ ...field, labelPosition })}
                    onWidthChange={labelWidth => onFieldChange({ ...field, labelWidth })}
                  />
                  <div className='flex items-center justify-between gap-2 text-xs text-muted-foreground'>
                    <span>{custom ? 'Custom' : 'Following form default'}</span>
                    {custom && (
                      <Button
                        type='button'
                        variant='link'
                        size='sm'
                        className='h-auto p-0 text-xs'
                        onClick={() => {
                          const { labelPosition: _position, labelWidth: _width, ...rest } = field
                          onFieldChange(rest)
                        }}
                      >
                        Use form default
                      </Button>
                    )}
                  </div>
                </div>
              )
            })()}
          </>
        )}

        {/* Placeholder */}
        {hasPlaceholder && (
          <div className='space-y-1.5'>
            <Label>Placeholder</Label>
            <Input
              value={field.placeholder}
              onChange={e => onFieldChange({ ...field, placeholder: e.target.value })}
              placeholder='Placeholder text…'
            />
          </div>
        )}

        {/* Help text — not on a text block, whose whole content is already on
            screen; a tooltip beside it would have nothing to add. */}
        {!isSection && !isConstant && (
          <div className='space-y-1.5'>
            <Label>Help Text</Label>
            <Input
              value={field.helpText}
              onChange={e => onFieldChange({ ...field, helpText: e.target.value })}
              placeholder='Additional guidance…'
            />
          </div>
        )}

        {/* Options source toggle for select */}
        {hasOptions &&
          (() => {
            const source = field.optionsSource ?? 'inline'
            const selectedSet = field.optionSetId
              ? optionSets.find(s => s.id === field.optionSetId)
              : undefined
            const resolvedFromSet = selectedSet
              ? selectedSet.options.map(o => ({ id: crypto.randomUUID(), label: o.label }))
              : []
            return (
              <div className='space-y-1.5'>
                <Label>Options source</Label>
                <div className='grid grid-cols-2 gap-2'>
                  <Button
                    variant={source === 'inline' ? 'default' : 'outline'}
                    size='sm'
                    onClick={() =>
                      onFieldChange({
                        ...field,
                        optionsSource: 'inline',
                        options: resolvedFromSet.length ? resolvedFromSet : field.options,
                        optionSetId: undefined,
                      })
                    }
                  >
                    Inline
                  </Button>
                  <Button
                    variant={source === 'optionSet' ? 'default' : 'outline'}
                    size='sm'
                    onClick={() => onFieldChange({ ...field, optionsSource: 'optionSet' })}
                  >
                    Option Set
                  </Button>
                </div>
              </div>
            )
          })()}

        {/* Option Set picker for select */}
        {hasOptions && (field.optionsSource ?? 'inline') === 'optionSet' && (
          <div className='space-y-1.5'>
            <Label>Option set</Label>
            {optionSets.length === 0 ? (
              <p className='rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground'>
                No option sets yet — create them in Configure → Option Sets.
              </p>
            ) : (
              <>
                <Select
                  value={field.optionSetId ?? undefined}
                  onValueChange={v => onFieldChange({ ...field, optionSetId: v })}
                >
                  <SelectTrigger className='h-8 text-sm'>
                    <SelectValue placeholder='Select an option set…' />
                  </SelectTrigger>
                  <SelectContent>
                    {optionSets.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.optionSetId && (
                  <p className='text-xs text-muted-foreground'>
                    {optionSets.find(s => s.id === field.optionSetId)?.options.length ?? 0} option
                    {(optionSets.find(s => s.id === field.optionSetId)?.options.length ?? 0) === 1
                      ? ''
                      : 's'}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Inline options editor for select */}
        {hasOptions && (field.optionsSource ?? 'inline') === 'inline' && (
          <div className='space-y-2'>
            <Label>Options</Label>
            <div className='space-y-2'>
              {field.options.map((opt, i) => (
                <InlineOptionRow
                  key={opt.id}
                  option={opt}
                  fieldId={field.id}
                  index={i}
                  count={field.options.length}
                  group={optionGroupId(field.id)}
                  locked={!!formInUse}
                  removeDisabled={field.options.length <= 1}
                  onLabelChange={label => {
                    const next = [...field.options]
                    next[i] = { ...opt, label }
                    onFieldChange({ ...field, options: next })
                  }}
                  onMove={(from, to) => {
                    const next = moveOption(field.options, from, to)
                    // Same reference on a no-op — skip the change so the form
                    // isn't marked dirty by a move that moved nothing (AC4.3).
                    if (next === field.options) return
                    onFieldChange({ ...field, options: next })
                  }}
                  onRemove={() =>
                    onFieldChange({
                      ...field,
                      options: field.options.filter(o => o.id !== opt.id),
                    })
                  }
                />
              ))}
              {formInUse && field.options.length > 0 && (
                <p className='text-xs text-muted-foreground'>
                  Option order is fixed once this form version has patient submissions.
                  Copy Procedure Template makes an editable copy.
                </p>
              )}
              <Button
                variant='outline'
                size='sm'
                className='w-full'
                onClick={() =>
                  onFieldChange({
                    ...field,
                    options: [
                      ...field.options,
                      {
                        id: crypto.randomUUID(),
                        label: `Option ${field.options.length + 1}`,
                      },
                    ],
                  })
                }
              >
                <Plus className='h-3.5 w-3.5 mr-1.5' />
                Add Option
              </Button>
            </div>
          </div>
        )}

        {!isSection && !isTable && (
          <>
            <Separator />
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
              CGT Settings
            </p>

            {/* Anchored toggle -- pin this field to the procedure header */}
            {!isCalculated && (
              <div className='flex items-center justify-between'>
                <div>
                  <Label className='font-normal cursor-pointer' htmlFor='anchored-toggle'>
                    Anchor to procedure header
                  </Label>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    Show this field at the top of the procedure, across all forms
                  </p>
                </div>
                <Switch
                  id='anchored-toggle'
                  checked={!!field.anchored}
                  onCheckedChange={v => onFieldChange({ ...field, anchored: v })}
                />
              </div>
            )}

            {/* LIS stage -- which laboratory order stage a mapped result is written for */}
            {supportsLisStage && (lisStages.length > 0 || !!field.lisStage) && (
              <div className='space-y-1.5'>
                <Label htmlFor='lis-stage-select'>Lab stage</Label>
                <LisStageSelect
                  id='lis-stage-select'
                  value={field.lisStage ?? null}
                  stages={lisStages}
                  onChange={v => onFieldChange({ ...field, lisStage: v ?? undefined })}
                />
                <p className='text-xs text-muted-foreground'>
                  A lab result mapped to this field is written only for an order placed at this
                  stage. Any stage is the default, used when no field for the same result is set
                  to the order&apos;s stage.
                </p>
              </div>
            )}

            {/* Connect test code -- the test this field's value represents, a label (z8nrz7g2wm) */}
            {supportsLisStage &&
              (testCodeChoices(lisStageTests, field.lisStage, field.lisTestCode).length > 0 ||
                !!field.lisTestCode) && (
                <div className='space-y-1.5'>
                  <Label htmlFor='lis-test-code-select'>Connect test code</Label>
                  <LisTestCodeSelect
                    id='lis-test-code-select'
                    value={field.lisTestCode ?? null}
                    stages={lisStageTests}
                    stage={field.lisStage}
                    onChange={v => onFieldChange({ ...field, lisTestCode: v ?? undefined })}
                  />
                  <p className='text-xs text-muted-foreground'>
                    The test this field&apos;s value represents, shown beneath the field on the
                    form. A label only: which value of a result is written here is set on the
                    integration&apos;s Mapping tab. Apply Test Results asks for every field
                    connected to a test code.
                  </p>
                </div>
              )}

            {/* Can change -- write-once entry, locked as soon as it holds a value */}
            {supportsCanChange && (
              <div className='flex items-center justify-between'>
                <div>
                  <Label className='font-normal cursor-pointer' htmlFor='can-change-toggle'>
                    Can change
                  </Label>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {field.canChange === false
                      ? 'Off — once this field has a value it can never be edited again'
                      : 'On — this field stays editable after it is filled in'}
                  </p>
                </div>
                <Switch
                  id='can-change-toggle'
                  // Absent means changeable: every field saved before this
                  // setting existed must keep behaving exactly as it did.
                  checked={field.canChange !== false}
                  onCheckedChange={v =>
                    onFieldChange({ ...field, canChange: v ? undefined : false })
                  }
                />
              </div>
            )}

            {/* Sequencing — lock this field until earlier field(s) are filled */}
            <div className='space-y-1.5'>
              <Label>Unlock after other fields are filled</Label>
              {earlierFields.length === 0 ? (
                <p className='rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground'>
                  No earlier fields to depend on.
                </p>
              ) : (
                <>
                  <MultiSelect
                    options={earlierFields.map(f => ({ id: f.id, label: f.label || 'Untitled' }))}
                    selected={selectedPrereqs}
                    onChange={ids =>
                      onFieldChange({ ...field, unlockedBy: ids.length ? ids : undefined })
                    }
                    placeholder='None — always unlocked'
                  />
                  <p className='text-xs text-muted-foreground'>
                    {selectedPrereqs.length === 0
                      ? 'Select one or more earlier fields; this field stays locked until they are all filled.'
                      : selectedPrereqs.length === 1
                        ? 'This field stays locked until the selected field has a value.'
                        : `This field stays locked until all ${selectedPrereqs.length} selected fields have values.`}
                  </p>
                  {showRequiredWarning && (
                    <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
                      A required field locked behind an optional field can block form completion
                      if that field is left empty.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Formula-driven unlock gate — supersedes the "unlock after" list
                above when set. Escape hatch for rules the simple prerequisite
                list can't express, e.g. a date-arithmetic condition. */}
            <GateFormulaEditor
              label='Unlock via formula instead'
              helperText='A Boolean formula that must be true for this field to unlock — replaces the prerequisite list above when on.'
              gate={field.unlockGate}
              onChange={gate => onFieldChange({ ...field, unlockGate: gate })}
              refGroups={fieldFormGroups}
              labelById={gateLabelById}
              blockedConsequence='this field stays locked'
            />

            {field.type === 'signature' && (
              <div className='space-y-2'>
                <UserGroupMultiSelect
                  id='field-allowed-signer-groups'
                  label='Allowed signers'
                  helpText='Only members of these groups may apply this signature, in person or through Sign as. Checked on the server.'
                  value={field.allowedSignerGroupIds ?? []}
                  onChange={ids => onFieldChange({ ...field, allowedSignerGroupIds: ids })}
                />
                {/* Four-eyes (z8nrz7dt44): signature fields sharing a set
                    name must each be signed by a different person. */}
                <div className='space-y-1.5'>
                  <Label htmlFor='field-distinct-signer-set' className='text-xs'>
                    Distinct signers
                  </Label>
                  <Input
                    id='field-distinct-signer-set'
                    list='distinct-signer-set-names'
                    value={field.distinctSignerSet ?? ''}
                    onChange={e => onFieldChange({ ...field, distinctSignerSet: e.target.value })}
                    placeholder='Set name, e.g. four-eyes'
                    maxLength={80}
                    className='h-8 text-sm'
                  />
                  <datalist id='distinct-signer-set-names'>
                    {distinctSetNames(allFields).map(n => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  {(() => {
                    const peers = distinctSetPeers(allFields, field)
                    return (
                      <p className='text-[11px] text-muted-foreground'>
                        {!field.distinctSignerSet?.trim()
                          ? 'Give two or more signature fields the same set name and each must be signed by a different person. Checked on the server.'
                          : peers.length === 0
                            ? 'No other signature field uses this set yet — on its own it signs as usual.'
                            : `Must be signed by someone other than the signer of: ${peers.map(p => p.label || 'Untitled').join(', ')}.`}
                      </p>
                    )
                  })()}
                </div>
                {/* The free-text group this replaces was never enforced — say
                    so rather than let an old value read as a restriction. */}
                {field.requiredGroup && (
                  <div className='rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800'>
                    Legacy group label “{field.requiredGroup}” was never enforced. Choose allowed
                    signers above to restrict this signature.{' '}
                    <button
                      type='button'
                      className='font-medium underline'
                      onClick={() => onFieldChange({ ...field, requiredGroup: undefined })}
                    >
                      Remove label
                    </button>
                  </div>
                )}
              </div>
            )}

            {field.type === 'signature' && (
              <GateFormulaEditor
                label='Signature gate'
                helperText='A Boolean formula that must be true before this signature can be applied — combined with the allowed signers above, and re-checked on the server.'
                gate={field.signatureGate}
                onChange={gate => onFieldChange({ ...field, signatureGate: gate })}
                refGroups={fieldFormGroups}
                labelById={gateLabelById}
                blockedConsequence='this signature cannot be applied'
              />
            )}

            {isCalculated && (
              <FormulaBuilder
                field={field}
                allFields={allFields}
                onChange={patch =>
                  onFieldChange({
                    ...field,
                    ...patch,
                    // A specification is written for a number or a date result.
                    // Switching between the two drops it, as a type change does.
                    ...('displayFormat' in patch &&
                    oosGradingType('calculated', patch.displayFormat) !== oosGradingType('calculated', field.displayFormat)
                      ? { oos: undefined }
                      : {}),
                  })
                }
                currentFormName={formMeta.name}
                crossForm={crossForm}
              />
            )}
          </>
        )}

        {/* Conditional visibility — value-driven show/hide (+ require) for this
            field. Separate from sequencing above: hides instead of locking. */}
        {!isSection && (
          <>
            <Separator />
            <VisibilityBuilder
              rule={field.visibility}
              onChange={rule => onFieldChange({ ...field, visibility: rule })}
              candidateFields={visibilitySources}
              formGroups={fieldFormGroups}
              targetLabel='field'
              // A text block is never required — it has nothing to fill in.
              allowRequire={!isCalculated && !isTable && !isConstant}
              pickArmed={visibilityPick}
              onArmPick={onVisibilityPickChange}
            />
          </>
        )}

        {/* Conditional notification — independent of visibility above. Fires
            on form submit, server-side, when this field's condition is met. */}
        {!isSection && (
          <>
            <Separator />
            <NotificationRuleBuilder
              rules={notificationRules(field)}
              // Written as the LIST, and the legacy single rule cleared in the
              // same patch — leaving it behind would mean a field carrying two
              // sources of truth for the same notifications.
              onChange={rules =>
                onFieldChange({ ...field, notifications: rules, notification: undefined })
              }
              candidateFields={notificationSources}
              formGroups={fieldFormGroups}
              recipientCandidates={notificationRecipients.data}
              recipientsLoading={notificationRecipients.isLoading}
              pickArmed={notificationPick}
              onArmPick={onNotificationPickChange}
            />
          </>
        )}

        {/* Out-of-specification grading (z8nrz7eq4e, z8nrz7eq4h). */}
        {oosGradingType(field.type, field.displayFormat) && (
          <>
            <Separator />
            <OosSpecBuilder
              spec={field.oos}
              // A calculated field is graded as its result: a number or a date.
              fieldType={oosGradingType(field.type, field.displayFormat) ?? field.type}
              allowComment={field.type !== 'calculated'}
              options={
                field.type !== 'select'
                  ? []
                  : (field.optionsSource ?? 'inline') === 'optionSet'
                    ? (optionSets.find(s => s.id === field.optionSetId)?.options ?? []).map(o => o.label)
                    : field.options.map(o => o.label)
              }
              onChange={oos => onFieldChange({ ...field, oos })}
            />
          </>
        )}
      </div>

      {/* Type-change confirmation (AC1.9/AC1.10) — always shown, even with no
          dependents, so the author sees the same gate every time. */}
      <Dialog open={pendingTypeChange !== null} onOpenChange={open => !open && setPendingTypeChange(null)}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Change field type?</DialogTitle>
            <DialogDescription>
              {field.label || 'This field'} will change from {FIELD_LABELS[field.type]} to{' '}
              {pendingTypeChange ? FIELD_LABELS[pendingTypeChange] : ''}. Its id, formulas and
              every other property stay untouched.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const dependents = pendingTypeChange ? findFieldDependents(allSections, field.id) : []
            return dependents.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No calculated fields, gates, visibility conditions or table-cell formulas on
                this form reference this field.
              </p>
            ) : (
              <div className='space-y-1.5'>
                <p className='text-sm font-medium'>
                  {dependents.length === 1 ? 'One dependent references' : `${dependents.length} dependents reference`}{' '}
                  this field:
                </p>
                <ul className='max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm'>
                  {dependents.map(d => (
                    <li key={`${d.surface}-${d.id}`}>
                      {d.label} <span className='text-muted-foreground'>— {DEPENDENT_SURFACE_LABELS[d.surface]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })()}
          <p className='text-xs text-muted-foreground'>
            Cross-form references from other forms in the patient journey can't be checked here.
          </p>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={() => setPendingTypeChange(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                // A specification is written for one type (ranges for a number,
                // rules for the rest), so it does not survive a change of type.
                if (pendingTypeChange)
                  onFieldChange({
                    ...field,
                    type: pendingTypeChange,
                    oos: sameOosOperators(pendingTypeChange, field.type) ? field.oos : undefined,
                  })
                setPendingTypeChange(null)
              }}
            >
              Change type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  )
}

/** True when a specification written for type `a` still reads the same on type `b`. */
function sameOosOperators(a: FieldType, b: FieldType): boolean {
  const ta = oosFieldType(a)
  const tb = oosFieldType(b)
  if (!ta || !tb) return false
  return ta === tb || (ta !== 'number' && tb !== 'number' && oosOperatorsFor(ta).join() === oosOperatorsFor(tb).join())
}
